<template>
  <div class="pa-park-list-view-set">
    <transition name="pa-simple-fade">
      <div class="list-view-set" v-if="listViewPosition === 'list'">
        <PAParkListView
         listType="foundedList"
         :parkList="parkList"
        />

        <PAParkListFetchButton
        :isLoading="isLoadingFetchButton"
        @handleClick="handleClickFetchButton"
        />
      </div>
    </transition>

    <transition name="pa-simple-fade">
      <div class="no-list-view-message" v-if="listViewPosition === 'no-message'">
        <PAParkListNoContentView
        :text="noList.text"
        :caption="noList.caption"
        :iconClass="noList.iconClass"
        >
          <template slot="icon">
            <PAParkSlashIcon />
          </template>
        </PAParkListNoContentView>
      </div>
    </transition>

    <PAParkSearchRequestButton />

    <PAParkListLoading :isLoading="isListViewLoading" />
  </div>
</template>

<script>
import PAParkListView           from '@/components/Parts/PAParkListView/PAParkListView.vue'
import PAParkListFetchButton    from '@/components/Parts/PAParkListFetchButton/PAParkListFetchButton.vue'
import PAParkListNoContentView  from '@/components/Pages/Parks/Finder/PAParkListNoContentView/PAParkListNoContentView.vue'
import PAParkListLoading        from '@/components/Parts/PAParkListLoading/PAParkListLoading.vue'

import PAParkSlashIcon          from '@/components/Pages/Parks/Finder/Images/park_slash.svg?inlineN'

import PAParkSearchRequestButton from '@/components/Pages/Parks/Finder/PAParkSearchRequestButton/PAParkSearchRequestButton.vue'

import {GetParkData, GetFoundedParks} from '@/graphql/queries/Park.graphql'
import {GetCharacterCouldRequestParkSearch} from '@/graphql/queries/Character.graphql'

export default {
  components : {
    PAParkListView,
    PAParkListFetchButton,
    PAParkListNoContentView,
    PAParkListLoading,
    PAParkSlashIcon,
    PAParkSearchRequestButton
  },

  data(){
    return {
      listViewPosition      : 'none',
      parkList              : null,
      isLoadingFetchButton  : false,
      isSearching           : false,

      noList                : {
        text      : '',
        caption   : '',
        iconClass : []
      },

      noListTemplate        : {
        noList    : {
          text      : 'まだみつかっていません。',
          caption   : '（おすすめのひろばが見つかると表示されます。）',
          iconClass : ['pa-icon-slash']
        },

        searching : {
          text      : 'ひろばを探索中です。',
          caption   : '（探索には最大2日程掛かります。）',
          iconClass : ['pa-icon-slash']
        }
      },
    }
  },

  apollo : {
    foundParks : {
      query : GetFoundedParks,
      
      variables(){
        return {
          page      : this.publicListViewPage,
          first     : 10,
          orderBy   : [
            {
              column : 'created_at',
              order : 'DESC'
            }
          ]
        };
      },

      result(result){
        const apolloClient = this.$apollo.provider.defaultClient;

        for(let data of result.data.foundParks.data){
          apolloClient.writeQuery({
            query     : GetParkData,
            variables : {
              id  : data.park.id
            },
            data      : {
              park : data.park
            }
          });
        }

        this.$store.dispatch({
          type      : 'parkList/updateListViewData',
          listType  : 'public',
          loaded    : true
        });
      },

      skip(){
        return (this.publicListViewPage === null);
      },

      error(err){
        const simpleErrorSet = this.getApolloSimpleErrorFromApolloError(err);
        
        this.apolloResultValidation(simpleErrorSet);
      },

      fetchPolicy: 'no-cache'
    },

    characterCouldRequestParkSearch : {
      query: GetCharacterCouldRequestParkSearch
    }
  },

  computed : {
    publicListViewData(){
      return this.$store.state.parkList.publicListViewData;
    },

    publicListViewPage(){
      return this.$store.state.parkList.publicListViewData.page;
    },

    publicListViewLoaded(){
      return this.$store.state.parkList.publicListViewData.loaded;
    },

    viewType(){
      return this.$route.params.viewType;
    },

    isListViewLoading(){
      if(typeof this.$apollo.queries.foundParks === 'undefined'){
        return false;
      }else{
        return this.$apollo.queries.foundParks.loading;
      }
    }
  },

  watch : {
    parkList : {
      handler(data){
        if(data === null){
          this.listViewPosition = 'none';
        }else if(data.length === 0 && this.publicListViewLoaded === false){
          this.listViewPosition = 'none';
        }else if(data.length === 0 && this.publicListViewLoaded === true){
          this.listViewPosition = 'no-message';
        }else{
          this.listViewPosition = 'list';
        }
      },
      immediate: true
    },

    publicListViewLoaded : {
      handler(value){
        if(this.parkList === null) return;
        
        if(value === true && this.parkList.length === 0){
          this.listViewPosition = 'no-message';
        }else if (value === false && this.parkList.length === 0){
          this.listViewPosition = 'none';
        }
      },
      immediate: true
    },

    publicListViewData  : {
      handler(data){
        if(typeof data === 'undefined') return;

        this.parkList = data.list;
      },
      immediate:true,
      deep:true
    },

    foundParks : {
      handler(data){
        if(typeof data === 'undefined') return;

        const listViewData = {
          list      : [],
          page      : null,
          lastPage  : null,
          total     : null
        };

        for(let data of data.data){
          listViewData.list.push({
            id        : data.park.id,
            expire_at : data.expire_at
          });
        }

        if(typeof data.paginatorInfo !== 'undefined'){
          listViewData.page     = data.paginatorInfo.currentPage;
          listViewData.lastPage = data.paginatorInfo.lastPage;
          listViewData.total    = data.paginatorInfo.total;
        }

        this.$store.dispatch({
          type      : 'parkList/updateListViewData',

          listType  : 'public',
          behaviour : 'push',
          
          list      : listViewData.list,
          page      : listViewData.page,
          lastPage  : listViewData.lastPage,
          total     : listViewData.total
        });
      },
      immediate: true
    },

    characterCouldRequestParkSearch : {
      handler(data){
        if(typeof data === 'undefined') return;

        this.isSearching = !data.result;
      },
      deep:true
    },

    isSearching : {
      handler(value){
        if(value === true){
          this.noList.text      = this.noListTemplate.searching.text;
          this.noList.caption   = this.noListTemplate.searching.caption;
          this.noList.iconClass = this.noListTemplate.searching.iconClass;
        }else{
          this.noList.text      = this.noListTemplate.noList.text;
          this.noList.caption   = this.noListTemplate.noList.caption;
          this.noList.iconClass = this.noListTemplate.noList.iconClass;
        }
      },
      immediate: true
    }
  },

  methods : {
    async handleClickFetchButton(){
      this.isLoadingFetchButton = true;

      await this.UXWaiting(500);

      if(!(this.publicListViewData.page >= this.publicListViewData.lastPage)){
        const nextPage = (this.publicListViewData.page + 1);

        this.$store.dispatch({
          type      : 'parkList/updateListViewData',
          listType  : 'public',
          page      : nextPage
        });
      }

      this.isLoadingFetchButton = false;
    }
  }
}
</script>

<style lang="scss">
@import "~bulma/sass/utilities/_all";
</style>